/**
 * ELCOMCouplingPage — Estuary, Lake and Coastal Ocean Model 3-D coupling
 *
 * Hodges & Dallimore 2006. Full ELCOM is a 3-D CFD; this page provides a
 * simplified 2-D plan-view flow-field analogue (vortex rings around plumes)
 * and exports an ELCOM-compatible boundary-condition file.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, ArrowLeft, Info, Download } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, onPersistChange } from '@/lib/sessionPersistence';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';
import { toast } from 'sonner';

const ARRAY_KEY = 'rb_module_array_v1';
interface ArrayCfg { moduleCount: number; cols: number; gapXM: number; gapYM: number; }
const ARR_DEFAULTS: ArrayCfg = { moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0 };

const FRAME_SIZE_M = 2.0;

const RESERVOIR_POLY: [number, number][] = [
  [85,18],[95,15],[112,14],[128,18],[142,24],[155,34],[163,48],[168,65],[170,82],
  [169,100],[166,118],[168,136],[170,155],[169,173],[167,192],[162,210],[156,228],
  [148,245],[138,262],[127,277],[116,290],[108,302],[102,315],[98,330],[94,345],
  [91,360],[89,375],[88,390],[87,403],[87,415],[85,432],[82,420],[79,408],[76,396],
  [73,383],[70,368],[67,352],[64,336],[60,320],[56,303],[51,287],[47,270],[42,253],
  [39,236],[37,218],[35,200],[34,182],[35,165],[38,148],[38,130],[36,112],[37,94],
  [40,77],[46,62],[52,48],[60,35],[72,25],
];

function designedPositions(a: ArrayCfg): { x: number; y: number }[] {
  const cols = Math.max(1, Math.min(6, a.cols));
  const rows = Math.ceil(a.moduleCount / cols);
  const pitchX = FRAME_SIZE_M + a.gapXM;
  const pitchY = FRAME_SIZE_M + a.gapYM;
  const out: { x: number; y: number }[] = [];
  const totalW = (cols - 1) * pitchX, totalH = (rows - 1) * pitchY;
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    if (r * cols + c >= a.moduleCount) break;
    out.push({ x: 95 + (c * pitchX - totalW / 2), y: 200 + (r * pitchY - totalH / 2) });
  }
  return out;
}

export default function ELCOMCouplingPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('elcom_coupling');
  const [array, setArray] = useState<ArrayCfg>(() => ({ ...ARR_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARR_DEFAULTS) }));
  const [windSpeed, setWind] = useState(3.5);
  const [windDir, setDir] = useState(240);     // ° from N
  const [gridRes, setGridRes] = useState(8);   // m per cell

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === ARRAY_KEY) setArray({ ...ARR_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARR_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const modules = useMemo(() => designedPositions(array), [array]);

  // Build a 2-D velocity field on the grid: surface drift from wind +
  // vortex rings around each module (idealised plume-surface flow signature)
  const flowField = useMemo(() => {
    const cellM = gridRes;
    const grid: { x: number; y: number; u: number; v: number }[] = [];
    const windRad = (windDir - 180) * Math.PI / 180;   // wind blows TO this direction
    const u_w = Math.sin(windRad) * windSpeed * 0.03;   // surface drift ~3% of U10
    const v_w = -Math.cos(windRad) * windSpeed * 0.03;
    for (let x = 35; x < 175; x += cellM)
      for (let y = 15; y < 432; y += cellM) {
        // Check if inside polygon (simplified bbox)
        let u = u_w, v = v_w;
        for (const m of modules) {
          const dx = x - m.x, dy = y - m.y;
          const r = Math.sqrt(dx * dx + dy * dy);
          if (r < 12 && r > 1) {
            // outward radial flow at the surface (boil) — Lemckert 2008
            const mag = 0.10 * Math.max(0, 1 - r / 12);
            u += (dx / r) * mag;
            v += (dy / r) * mag;
          }
        }
        grid.push({ x, y, u, v });
      }
    return grid;
  }, [modules, windSpeed, windDir, gridRes]);

  const exportSetup = () => {
    const setup = `# ELCOM Setup — Barcombe Reservoir (auto-generated)
# Reference: Hodges & Dallimore 2006

[grid]
horizontal_resolution_m = ${gridRes}
vertical_resolution_m = 0.2
domain_size_m = 140 × 420 × 4.5

[boundary_conditions]
wind_speed_m_s = ${windSpeed.toFixed(2)}
wind_direction_deg = ${windDir}
air_temperature_C = 17
solar_radiation_W_m2 = 200

[bubble_plume_sources]
n_sources = ${modules.length}
# Each plume modelled as a body force at the surface (Wuest 1992)
${modules.map((m, i) => `source[${i}] = { x: ${m.x.toFixed(1)}, y: ${m.y.toFixed(1)}, z: 0.4, buoyancy_flux_m4_s3: 0.0003 }`).join('\n')}

[output]
write_velocity_field = true
write_temperature_field = true
write_dye_tracer = true
output_interval_s = 3600
`;
    const blob = new Blob([setup], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'elcom_barcombe.txt';
    a.click();
    toast.success('ELCOM setup file exported');
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Box}
        title="Module — ELCOM 3-D Hydrodynamic Coupling"
        subtitle="Hodges & Dallimore 2006. 2-D plan-view flow field + ELCOM setup export"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('elcom_coupling')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={exportSetup}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500">
              <Download className="w-4 h-4" /> Export setup
            </button>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="elcom_coupling" />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Wind speed U10 (m/s)</label>
          <input type="range" min={0} max={12} step={0.5} value={windSpeed}
            onChange={e => setWind(Number(e.target.value))} className="w-full accent-sky-500" />
          <div className="text-xs text-slate-100">{windSpeed.toFixed(1)} m/s</div>
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Wind direction (° from N)</label>
          <input type="range" min={0} max={359} step={5} value={windDir}
            onChange={e => setDir(Number(e.target.value))} className="w-full accent-sky-500" />
          <div className="text-xs text-slate-100">{windDir}°</div>
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Grid resolution (m)</label>
          <input type="range" min={4} max={20} step={2} value={gridRes}
            onChange={e => setGridRes(Number(e.target.value))} className="w-full accent-sky-500" />
          <div className="text-xs text-slate-100">{gridRes} m</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Surface flow field (plan view)</div>
        <svg viewBox="20 0 170 450" className="w-full max-w-md mx-auto h-auto bg-slate-950 rounded">
          <polygon points={RESERVOIR_POLY.map(p => p.join(',')).join(' ')}
            fill="rgba(56, 189, 248, 0.08)" stroke="#38bdf8" strokeWidth={1} />
          {/* Flow vectors */}
          {flowField.map((c, i) => {
            const mag = Math.sqrt(c.u * c.u + c.v * c.v);
            if (mag < 0.01) return null;
            const scale = 25;
            const x2 = c.x + c.u * scale;
            const y2 = c.y + c.v * scale;
            const colour = mag > 0.1 ? '#22c55e' : mag > 0.05 ? '#fbbf24' : '#94a3b8';
            return (
              <line key={i} x1={c.x} y1={c.y} x2={x2} y2={y2}
                stroke={colour} strokeWidth={0.6} opacity={0.7} />
            );
          })}
          {/* Modules */}
          {modules.map((m, i) => (
            <g key={i}>
              <circle cx={m.x} cy={m.y} r={2.5} fill="#fbbf24" stroke="#0a1628" strokeWidth={0.5} />
              <text x={m.x + 3} y={m.y + 3} fontSize={3.5} fill="#fcd34d">M{i + 1}</text>
            </g>
          ))}
          {/* Wind direction arrow */}
          {(() => {
            const wx = (windDir - 180) * Math.PI / 180;
            const cx0 = 150, cy0 = 35, len = 18;
            return (
              <g>
                <line x1={cx0} y1={cy0} x2={cx0 + Math.sin(wx) * len} y2={cy0 - Math.cos(wx) * len}
                  stroke="#a78bfa" strokeWidth={2} markerEnd="url(#wind-arr)" />
                <text x={cx0 + 4} y={cy0 - 12} fontSize={6} fill="#a78bfa">wind</text>
              </g>
            );
          })()}
          <defs>
            <marker id="wind-arr" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={4} markerHeight={4} orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" fill="#a78bfa" />
            </marker>
          </defs>
        </svg>
        <div className="text-[10px] text-slate-500 mt-1 text-center">
          Surface velocity vectors. Green = strong outward flow (module boils). Amber = transition.
          Slate = ambient wind drift. {flowField.length} grid cells at {gridRes} m resolution.
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            <strong className="text-emerald-300">In-browser preview:</strong> 2-D plan-view flow field with
            wind-driven surface drift (Stommel / Ekman, U_surf ≈ 3% × U10) superimposed on outward radial
            flow signatures around each module (Lemckert 2008 fountain-like upwelling). Useful for
            visualising plume-plume interaction and identifying convergence/divergence zones.
          </p>
          <p>
            <strong className="text-sky-300">External ELCOM run:</strong> the "Export setup" button writes
            an ELCOM-compatible boundary-condition file with each module as a buoyancy-flux source.
            Combine with the bathymetric grid and the meteorological forcing to run the full 3-D model
            and verify the in-browser preview.
          </p>
          <p className="italic text-slate-500">References: Hodges & Dallimore 2006; Lemckert 2008.</p>
        </div>
      </div>
    </div>
  );
}
